import { merge } from 'lodash';
import React, { useMemo, useCallback } from 'react';

import { TenantSortFields, SortDirection, GetTenantsQueryVariables } from '@/graphql';
import { BaseTableProps } from '@boilerplate/types/entity';
import Entity from '@/entities/tenant';
import EntityTable, {
  defaultPage,
  defaultPageSize,
  defaultSortOrder,
  getDefaultSortField,
} from '@boilerplate/components/entity/EntityTable/EntityTable';
import { canPerformAction, useAuthenticatedUserStore } from '@/stores/UserStore';
import { openConfirmationDialog } from '@/stores/ConfirmationDialogStore';


export const useTenantsTable = ({ queryOptions }: { queryOptions?: GetTenantsQueryVariables } = {}) => {
  const defaultVariableOptions = useMemo<GetTenantsQueryVariables>(
    () => 
      merge(
        {
          sorting: {
            direction: defaultSortOrder === 'desc' ? SortDirection.Desc : SortDirection.Asc,
            field: getDefaultSortField(Entity.table.schema) as TenantSortFields,
          },
          paging: {
            page: defaultPage,
            pageSize: defaultPageSize,
          },
                  withMailLayouts: true,
          },
          queryOptions,
        ), 
    [queryOptions]
    );
  
  const {
    data: tenantData,
    items = [],
    loading,
    refetch: originalRefetch,
    variables,
  } = Entity.model.useGetAll({
    fetchPolicy: 'cache-and-network',
    variables: defaultVariableOptions,
  });

  const [remove] = Entity.model.useDelete();

  const refetch = useCallback(
    (variableOptions?: Partial<GetTenantsQueryVariables >) => {
      return originalRefetch(merge({}, defaultVariableOptions, variableOptions));
    },
    [defaultVariableOptions, originalRefetch]
  );

  const allActions = useMemo(() => ({ loading, refetch, remove }), [loading, refetch, remove]);
  const data = useMemo(
    () =>
      tenantData
        ? { totalCount: tenantData.tenants.totalCount, rows: items }
        : { totalCount: 0, rows: [] },
    [tenantData, items]
  );

  return { data, actions: allActions, refetch, defaultVariableOptions, variables };
}

export type TenantsBaseTableProps = BaseTableProps<typeof Entity.model>;

export default function TenantsBaseTable(props: TenantsBaseTableProps) {
  const tableProps = useTenantsTable();
  const authentictedUser = useAuthenticatedUserStore();

  return (
    <EntityTable
      {...props}
      {...tableProps}
      Entity={Entity}
      user={authentictedUser}
      canPerformThisAction={canPerformAction}
      openConfirmationDialog={openConfirmationDialog}
    />
  );
}
